import * as React from "react";
import { useSelector } from "react-redux";
import {
  selectAxisLabelOffset3d,
  selectGeometryDebug3d,
  selectLightPitch3d,
  selectLightYaw3d,
  selectRender3dEngine,
  selectRightHand3d,
  selectShowLightDirection3d,
} from "../../features/ui/uiSlice";
import { ngLightDirection, Vec3 } from "./extrusionEdges";
import { DiagramResults } from "./drawSectionForceDiagrams";
import type { GraphPointsMode } from "../Viewport";
import { SvgIsometricColumnView } from "./SvgIsometricColumnView";
import { ThreeColumnView } from "./ThreeColumnView";
import { isometric_3d } from "../ViewportStyles";

interface View3DProps {
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
}

interface ScreenPoint {
  x: number;
  y: number;
}

// ng directions on the overlay's screen: x up, y/z on the two diagonals (40px per unit)
const TRIAD_ORIGIN = { x: 44, y: 58 };
const triadPoint = (v: Vec3, length: number, rightHand: boolean): ScreenPoint => ({
  x: TRIAD_ORIGIN.x + length * (rightHand ? v.z - v.y : v.y - v.z) * Math.cos(Math.PI / 6),
  y: TRIAD_ORIGIN.y + length * ((v.y + v.z) * Math.sin(Math.PI / 6) - v.x),
});

const AxisTriad = (props: { origin: ScreenPoint | null; labelOffset: number; light: Vec3 | null; rightHand: boolean }) => (
  <svg
    aria-label="3D orientation axes"
    role="img"
    viewBox="0 0 96 96"
    style={{
      position: "absolute",
      top: props.origin ? props.origin.y - 58 : undefined,
      bottom: props.origin ? undefined : 8,
      left: props.origin?.x ?? "50%",
      width: 96,
      height: 96,
      transform: "translateX(-44px)",
      pointerEvents: "none",
      overflow: "visible",
    }}
  >
    <defs>
      <marker id="axis-arrow-x" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M 0 0 L 6 3 L 0 6 Z" fill={isometric_3d.axis.xColor} />
      </marker>
      <marker id="axis-arrow-y" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M 0 0 L 6 3 L 0 6 Z" fill={isometric_3d.axis.yColor} />
      </marker>
      <marker id="axis-arrow-z" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M 0 0 L 6 3 L 0 6 Z" fill={isometric_3d.axis.zColor} />
      </marker>
      <marker id="axis-arrow-light" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
        <path d="M 0 0 L 6 3 L 0 6 Z" fill={isometric_3d.axis.lightColor} />
      </marker>
    </defs>
    <g fill="none" strokeWidth="2" strokeLinecap="round">
      <line x1="44" y1="58" x2="44" y2="17" stroke={isometric_3d.axis.xColor} markerEnd="url(#axis-arrow-x)" />
      <line
        x1="44"
        y1="58"
        x2={props.rightHand ? 9 : 79}
        y2="78"
        stroke={isometric_3d.axis.yColor}
        markerEnd="url(#axis-arrow-y)"
      />
      <line
        x1="44"
        y1="58"
        x2={props.rightHand ? 79 : 9}
        y2="78"
        stroke={isometric_3d.axis.zColor}
        markerEnd="url(#axis-arrow-z)"
      />
    </g>
    {props.light &&
      (() => {
        const end = triadPoint(props.light, 40, props.rightHand);
        const foot = triadPoint({ ...props.light, x: 0 }, 40, props.rightHand);
        return (
          <g fill="none" strokeLinecap="round">
            <line
              x1={end.x}
              y1={end.y}
              x2={foot.x}
              y2={foot.y}
              strokeWidth="1"
              strokeDasharray="3 3"
              stroke={isometric_3d.axis.lightProjectionColor}
            />
            <line
              x1={TRIAD_ORIGIN.x}
              y1={TRIAD_ORIGIN.y}
              x2={end.x}
              y2={end.y}
              strokeWidth="2"
              strokeDasharray={props.light.x < 0 ? "4 3" : undefined}
              stroke={isometric_3d.axis.lightColor}
              markerEnd="url(#axis-arrow-light)"
            />
          </g>
        );
      })()}
    <circle cx="44" cy="58" r="2.5" fill="var(--sx-viewport-black)" />
    <g fontSize="12" fontWeight="600" fontFamily="inherit" textAnchor="middle">
      <text x={44 + props.labelOffset} y="11" fill="var(--sx-viewport-black)">
        X
      </text>
      <text x={props.rightHand ? 3 : 86} y={86 + props.labelOffset} fill="var(--sx-viewport-black)">
        Y
      </text>
      <text x={props.rightHand ? 86 : 3} y={86 + props.labelOffset} fill="var(--sx-viewport-black)">
        Z
      </text>
    </g>
  </svg>
);

export const View3D = (props: View3DProps) => {
  const engine = useSelector(selectRender3dEngine);
  const rightHand = useSelector(selectRightHand3d);
  const axisLabelOffset = useSelector(selectAxisLabelOffset3d);
  const geometryDebug = useSelector(selectGeometryDebug3d);
  const showLightDirection = useSelector(selectShowLightDirection3d);
  const lightYaw = useSelector(selectLightYaw3d);
  const lightPitch = useSelector(selectLightPitch3d);
  const light = showLightDirection ? ngLightDirection(lightYaw, lightPitch) : null;
  const lightRay = light && { x: -light.x, y: -light.y, z: -light.z };
  const [axisOrigin, setAxisOrigin] = React.useState<ScreenPoint | null>(null);
  const updateAxisOrigin = React.useCallback((value: ScreenPoint) => {
    setAxisOrigin((old) =>
      old !== null && Math.abs(old.x - value.x) < 0.5 && Math.abs(old.y - value.y) < 0.5 ? old : value,
    );
  }, []);
  const view =
    engine === "three" ? (
      <ThreeColumnView {...props} onAxisOriginScreenChange={updateAxisOrigin} />
    ) : (
      <SvgIsometricColumnView {...props} />
    );

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {view}
      {engine === "three" && !geometryDebug && (
        <AxisTriad origin={axisOrigin} labelOffset={axisLabelOffset} light={lightRay} rightHand={rightHand} />
      )}
    </div>
  );
};
